import React from 'react';
import './Explainer.css';

import UserGroups from './UserGroups';
import { APP_STORE_URL } from './InstallBanner';

// WEB-EVENTCOUNT-1. The count arrives from /config, which is fetched on mount —
// so the landing page renders at least once with it still undefined, and again
// with the real number. "thousands of" is what fills that gap: vague on purpose,
// and true (the real count has been 2–5k every year), so the swap-in never reads
// as a correction. It is a shared constant rather than two string literals
// because the page says the number TWICE, one line apart, and a mismatch there
// is the kind of thing nobody notices in review.
export const EVENTS_COUNT_PLACEHOLDER = 'thousands of';

// Grouped, because "2380 events" on a landing page reads like a serial number.
export const formatEventsCount = (eventsCount) =>
  typeof eventsCount === 'number' && eventsCount > 0
    ? eventsCount.toLocaleString()
    : EVENTS_COUNT_PLACEHOLDER;

const Explainer = ({ user, eventsCount }) => {
  const count = formatEventsCount(eventsCount);
  return (
    <div className="explainer">
      <blockquote className="quote">
        “No wise fish would go anywhere without a porpoise.”<br />
        ― Lewis Carroll, <em>Alice in Wonderland</em>
      </blockquote>
      
      <div>
        <p>Burning Man has {count} events. There is something for everyone, but not everything is for everyone.</p>
        <p><b>What do you want to do about it?</b></p>
        <div>
          Options:
          <ol type='a'>
            <li>Explore each of {count} events and meticulously plan the itinerary</li>
            <li>Just go there and wander around, hoping that cool stuff will find me</li>
            <li>
              Use Naviguesser to<br/>
              <ol className="nested-options">
                <li>Check just a few events to get excited</li>
                <li>Get hyped about what my crew is into</li>
                <li>Take a list of must-sees with me</li>
              </ol>
            </li>
          </ol>
        </div>
      </div>

      <p><b>How does Naviguesser work?</b></p>
      <div>
        <ol>
          <li>It shuffles events and art, you check them out one item at a time and decide if you'd be interested</li>
          <li>You can share a link with your crew, and then their selection will be combined with yours</li>
          <li>Export your picks to <a href="https://dust.events" target="_blank" rel="noopener noreferrer">Dust</a> mobile app to use during Burning Man</li>
        </ol>
        If you and 5 friends select 10-15 events each, you will have more choices than you can visit.
      </div>

      <p><b>What's the point?</b></p>
      <ul>
        <li>If you have no plan at all, some of the most interesting stuff will always be happening right around the corner.</li>
        <li>If you plan too much, where's the fun in that? Plans don't work at BM anyway, so why bother trying too hard?</li>
        <li>
          Naviguesser tries to balance immediacy and self-reliance with some help of communal effort with an experimental approach.
          <ul>
            <li>Having some list helps you structure your journey, but will never prevent you from detours</li>
            <li>Having a shared list with friends helps you know them a bit better, and adds an element of surprise</li>
            <li>Shuffle helps to fill the list evenly</li>
            <li>Yet, your efforts are up to you: you decide how many to explore</li>
          </ul>
        </li>
      </ul>

      <div><b>Do you want to try?</b>

      <UserGroups user={user} />      
      </div>

      {/* WEBUX-1: InstallBanner is the only other App Store link on the site and isIOS() hides it,
          so before this every desktop and Android visitor had NO path to the app at all. This one
          is unconditional — someone reading the pitch on a laptop should still be able to find it. */}
      <p className="explainer-app-link">
        Naviguesser is on iPhone and Apple Watch —{' '}
        <a href={APP_STORE_URL} target="_blank" rel="noopener noreferrer">
          get it on the App Store
        </a>.
      </p>

    </div>
  );
};

export default Explainer;
